<script setup lang="ts">
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { fetchInvoiceQueryOptions } from '@/features/invoices/queries'
import InvoiceSkeleton from '@/features/invoices/components/partials/InvoiceSkeleton.vue'
import InvoiceSkeletonInvoiceDetails from '@/features/invoices/components/partials/InvoiceSkeletonInvoiceDetails.vue'
import PageContent from '@/components/page-content.vue'
import InvoiceSkeletonBankingDetails from '@/features/invoices/components/partials/InvoiceSkeletonBankingDetails.vue'
import InvoiceSkeletonSummary from '@/features/invoices/components/partials/InvoiceSkeletonSummary.vue'
import { computed, reactive, ref, useTemplateRef, watchEffect } from 'vue'
import { watchOnce } from '@vueuse/core'
import InvoiceSkeletonOwnerDetails from '@/features/invoices/components/partials/InvoiceSkeletonOwnerDetails.vue'
import InvoiceSkeletonBillTotal from '@/features/invoices/components/partials/InvoiceSkeletonBillTotal.vue'
import InvoiceSkeletonCharges from '@/features/invoices/components/partials/InvoiceSkeletonCharges.vue'
import InvoiceSkeletonMeterReadings from '@/features/invoices/components/partials/InvoiceSkeletonMeterReadings.vue'
import InvoiceSkeletonCustomerDetails from '@/features/invoices/components/partials/InvoiceSkeletonCustomerDetails.vue'
import ButtonPrimary from '@/components/button-primary.vue'
import { faArrowLeft, faFloppyDisk, faHandPointUp, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { useAddInvoiceCharges, useUpdateInvoice, useUpdateInvoiceCharges, useUpdateInvoiceCategories } from '@/features/invoices/mutations'
import useLoader from '@/composables/loader'
import useNotifications from '@/composables/notifications'
import { ChargeType, InvoiceStatusNumeric } from '@/features/invoices/constants'
import { useRouter } from 'vue-router'
import { useUpdateInvoiceMeterReadings, useSaveNotes } from '@/composables/billing/invoices'
import ButtonSecondary from '@/components/button-secondary.vue'
import ButtonHighlighted from '@/components/button-highlighted.vue'
import ModalEscalationWarning from '@/views/billing/invoicing/modal-escalation-warning.vue'
import ContentLoader from '@/components/content-loader.vue'
import HeroSplashError from '@/components/hero-splash-error.vue'
import ModalConfirmApproveAndSend from '@/views/billing/invoicing/modal-confirm-approve-and-send.vue'
import { useModalState } from '@/shared/composables/useModalState'
import InvoiceSkeletonCreditNotes from '@/features/invoices/components/partials/InvoiceSkeletonCreditNotes.vue'
import { useReplaceCreditNoteAllocations } from '@/features/invoices/composables/use-replace-credit-note-allocations'
import { useApproveInvoice } from '@/features/invoices/mutations/invoices'
import type { ComponentProps } from '@/types'
import InvoiceApprovalAffectedByCreditNoteErrorsModal
  from '@/views/billing/invoicing/InvoiceApprovalAffectedByCreditNoteErrorsModal.vue'
import {
  getFailedCreditNotesForInvoiceApprovalWarning
} from '@/features/invoices/lib/getFailedCreditNotesForInvoiceApprovalWarning'
import { ApiError, CompositeResponseError } from '@/shared/api/validation'
import { shouldShowApproveInvoiceWarningModal } from '@/features/invoices/lib/shouldShowApproveInvoiceWarningModal'
import { assetQueries } from '@/features/assets/queries'
import { AssetType } from '@/features/assets/constants'
import InvoiceSkeletonSummaryFullWidth
  from '@/features/invoices/components/partials/InvoiceSkeletonSummaryFullWidth.vue'
import InvoiceSkeletonWheelingCharges from './partials/InvoiceSkeletonWheelingCharges.vue'
import { watchEffectUntilStopped } from '@/shared/composables/watchEffectUntilStopped'
import InvoiceSkeletonNotes from '@/features/invoices/components/partials/InvoiceSkeletonNotes.vue'
import type { invoiceSchema } from '../schemas'
import { type z } from 'zod'

type Category = z.infer<typeof invoiceSchema>['categories'][number]
type Charge = Category['charges'][number]

type ChargesComponent = {
  changes: () => {
    charges: Array<{
      id: number,
      values: Charge,
      categoryId?: number
    }>,
    categories: Array<{
      id: number,
      values: Category,
    }>
  }
  clearChanges: () => void
}
import { getInvoiceApprovalErrorMessage } from '@/features/invoices/lib/getInvoiceApprovalErrorMessage'
import { creditNoteQueryKeys, invoiceQueryKeys } from '@/features/invoices/query-keys'

const props = defineProps<{ invoiceId: number }>()
const { data: invoice, isError, error, isFetching } = useQuery(fetchInvoiceQueryOptions(props.invoiceId))
const { data: assetDetails } = useQuery(assetQueries.details(() => invoice.value?.assetCode))

const queryClient = useQueryClient()
const router = useRouter()
const { wrap } = useLoader()
const { addSuccess, addError, addWarning } = useNotifications()
const { open: showApproveAndSendConfirmation, isOpen: isConfirmingApproveAndSend, close: hideApproveAndSendConfirmation } = useModalState()
const { mutateAsync: approveInvoiceAsync } = useApproveInvoice()
const { mutateAsync: updateInvoiceAsync } = useUpdateInvoice(props.invoiceId, 'never')
const { mutateAsync: updateCategoriesAsync } = useUpdateInvoiceCategories(props.invoiceId, 'never')
const { mutateAsync: updateChargesAsync } = useUpdateInvoiceCharges(props.invoiceId, 'never')
const { mutateAsync: addChargesAsync } = useAddInvoiceCharges(props.invoiceId, 'never')
const { mutateAsync: updateMeterReadingsAsync } = useUpdateInvoiceMeterReadings(props.invoiceId, true, 'never')
const { mutateAsync: updateNotesAsync } = useSaveNotes(props.invoiceId, 'never')
const { mutateAsync: updateCreditNoteAllocationsAsync } = useReplaceCreditNoteAllocations(props.invoiceId, 'never')
const chargesComponent = useTemplateRef<ChargesComponent>('charges')
const metersComponent = useTemplateRef<InstanceType<typeof InvoiceSkeletonMeterReadings>>('meters')
const creditNotesComponent = useTemplateRef<InstanceType<typeof InvoiceSkeletonCreditNotes>>('creditNotes')
const showSavings = ref(true)
const poNumber = ref<string|null>('')
const recsGenerated = ref<number>()
const showMeters = ref(true)
const notes = ref<string|null>(null)

const currentCreditNoteAllocationState = ref<{ error: boolean, amountInCents: number }>()
const escalationWarning = ref<string>()
const flattenedCharges = computed(() => invoice.value?.categories.flatMap(x => x.charges) ?? [])

// Update some values when the invoice loads initially.
watchEffectUntilStopped(
  stop => {
    if (invoice.value === undefined) {
      return
    }

    poNumber.value = invoice.value.poNumber
    showSavings.value = invoice.value.showSavings
    showMeters.value = invoice.value.showMeterReadings
    notes.value = invoice.value.invoiceNotes
    recsGenerated.value = invoice.value.recsGenerated ?? 0
    stop()
  }
)

const invoiceAmounts = computed(
  () => {
    const totalCreditedAmountInCents = currentCreditNoteAllocationState.value?.amountInCents ?? (invoice.value?.totalCreditedInRands ?? 0)
    const amountDueInCents = (
      currentCreditNoteAllocationState.value === undefined
        ? (invoice.value?.totalAmountDueInRands ?? 0) * 100
        : (invoice.value?.totalAmountIncludingTax ?? 0) * 100 - currentCreditNoteAllocationState.value.amountInCents
    )

    return {
      totalCreditedAmountInCents,
      amountDueInCents,
    }
  }
)
const approvalWarningModal = reactive(useModalState<ComponentProps<typeof InvoiceApprovalAffectedByCreditNoteErrorsModal>>())
const navigateToInvoiceDashboard = () => router.push({ name: 'invoices.list' })

function approveInvoice() {
  return wrap(
    () => approveInvoiceAsync(props.invoiceId)
  ).then(
    () => {
      addSuccess({ title: 'Invoice approved successfully.' })
    },
    (err: unknown) => {
      let extraErrorDetail: string|undefined = undefined

      if (err instanceof ApiError) {
        const escalationTaskError = err.errors.find(e => e.pointer?.toLowerCase() == 'escalationid')

        if (escalationTaskError) {
          escalationWarning.value = escalationTaskError.message
          return
        }
      }

      if (shouldShowApproveInvoiceWarningModal(err)) {
        approvalWarningModal.open({
          creditNoteNames: getFailedCreditNotesForInvoiceApprovalWarning(err),
          invoiceId: props.invoiceId,
          projectCode: invoice.value!.projectCode,
          assetCode: invoice.value!.assetCode,
          projectName: invoice.value!.projectName
        })
      } else {
        extraErrorDetail = getInvoiceApprovalErrorMessage(err)
      }

      addError({
        title: 'Invoice could not be approved.',
        message: extraErrorDetail,
        hideDelay: extraErrorDetail ? 10000 : undefined
      })
    }
  )
}

async function persistChanges() {

  const statusBeforeSave = invoice.value?.invoiceStatusId

  const changedMeters = metersComponent.value?.changes() ?? []
  const changedCharges = chargesComponent.value?.changes().charges ?? []
  const changedCategories = chargesComponent.value?.changes().categories ?? []
  const creditNoteAllocations = creditNotesComponent.value?.allocations() ?? []
  let promiseKeys: string[]
  let promiseFns: Promise<unknown>[]

  await wrap(
    () => {
      const promises: Record<string, Promise<unknown>> = {
        'Invoice details': updateInvoiceAsync({
          showSavings: showSavings.value,
          poNumber: poNumber.value,
          showMeterReadings: showMeters.value,
          recsGenerated: recsGenerated.value ?? 0
        })
      }

      if (assetDetails.value?.type === AssetType.Wheeling) {
        promises['Credit note allocations'] = updateCreditNoteAllocationsAsync(
          creditNoteAllocations.map(
            x => ({ creditNoteId: x.creditNoteId, allocatedAmountInCents: x.amountInCents })
          )
        )
      }

      if (notes.value !== null) {
        promises['Notes'] = updateNotesAsync(notes.value)
      }

      if (changedCategories.length > 0) {
        promises['Categories'] = updateCategoriesAsync(
          changedCategories.map(
            x => ({
              ...x.values,
              id: x.id,
            })
          )
        )
      }

      if (changedCharges.length > 0) {
        const updatedCharges = changedCharges.filter(x => x.id > 0)
        const newCharges = changedCharges.filter(x => x.id <= 0)

        if (updatedCharges.length > 0) {
          promises['Updated charges'] = updateChargesAsync(
            updatedCharges.map(
              x => ({
                ...(flattenedCharges.value.find(y => y.id === x.id) ?? {}),
                ...x.values,
                id: x.id,
              })
            )
          )
        }

        if (newCharges.length > 0) {
          promises['New charges'] = addChargesAsync(
            newCharges.map(
              x => ({
                invoiceId: invoice.value?.id!,
                categoryId: x.categoryId!,
                chargeType: x.values.chargeType ?? ChargeType.WheelingUsageFirm,
                description: x.values.description!,
                rateInCents: x.values.rateInCents!,
                quantity: x.values.quantity!,
                isHidden: x.values.hidden!,
                seasonId: x.values.seasonId!,
                demandTypeId: x.values.demandTypeId!
              })
            )
          )
        }
      }

      if (changedMeters.length > 0) {
        promises['Meter readings'] = updateMeterReadingsAsync(
          changedMeters.map(
            changed => {
              const existing = invoice.value!.meterReadings.find(meter => meter.id === changed.id)!

              return {
                ...existing,
                ...changed.values,
                id: changed.id,
              }
            }
          )
        )
      }

      const entries = Object.entries(promises)
      promiseKeys = entries.map(([key]) => key)
      promiseFns = entries.map(([, fn]) => fn)

      return Promise.allSettled(promiseFns)
    }
  ).then(
    async resolutions => {
      // All failed. Show the failure message.
      if (resolutions.every(x => x.status === 'rejected')) {
        addError({ title: 'Unable to update invoice', message: 'Please try again' })
        return
      }

      // All successful. Clear what we need to.
      if (resolutions.every(x => x.status === 'fulfilled')) {
        addSuccess({ title: 'Invoice updated successfully' })

        // Clear all the query keys used in the mutations.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.details(props.invoiceId) }),
          queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.invoice(props.invoiceId) }),
          queryClient.invalidateQueries({ queryKey: creditNoteQueryKeys.allocatable(props.invoiceId) }),
          queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.paginated() }),
          queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.invoiceMeterReadings(props.invoiceId) }),
        ])

        metersComponent.value?.clearChanges()
        chargesComponent.value?.clearChanges()

        if (statusBeforeSave !== InvoiceStatusNumeric.Draft) {
          await navigateToInvoiceDashboard()
        }

        return
      }

      // Otherwise, it was a partial success. We need to indicate this with a warning and indicate which
      // parts couldn't be saved.
      const failedKeys = promiseKeys.reduce(
        (accumulator, current, currentIndex) =>
          resolutions[currentIndex].status === 'fulfilled' ? accumulator : accumulator.concat(current),
        [] as string[]
      )

      addWarning({
        title: 'Invoice partially updated',
        allowHtml: true,
        hideDelay: 20_000,
        message: `
          <p>Some parts of the invoice failed to save successfully.</p>

          <p class="mb-0">
            The following failed to save:
            <ul><li>${failedKeys.join('</li><li>')}</li></ul>
          </p>

          <p>Please check the contents of the invoice for errors and try again.</p>
        `
      })

      // Clear everything except the invoice details - the clearing of the invoice details is what loses the charges.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: creditNoteQueryKeys.allocatable(props.invoiceId) }),
        queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.paginated() }),
        queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.invoiceMeterReadings(props.invoiceId) }),
      ])
    }
  )
}

</script>

<template>
  <PageContent>
    <ModalEscalationWarning v-if="escalationWarning !== undefined" :message="escalationWarning" @close="escalationWarning = undefined" />
    <ModalConfirmApproveAndSend v-if="isConfirmingApproveAndSend"
                                @close="hideApproveAndSendConfirmation"
                                :invoice-id="invoiceId" />

    <ContentLoader v-if="isFetching" :loading="isFetching" />
    <HeroSplashError v-else-if="isError" />
    <InvoiceSkeleton v-else-if="invoice" title="Tax Invoice">
      <template #footer >
        <template v-if="![InvoiceStatusNumeric.Approved, InvoiceStatusNumeric.Sent].includes(invoice.invoiceStatusId)">
          <ButtonPrimary :disabled="currentCreditNoteAllocationState?.error" @click="persistChanges()" :icon="faFloppyDisk">Save</ButtonPrimary>
          <ButtonSecondary :icon="faXmark" @click="navigateToInvoiceDashboard">Cancel</ButtonSecondary>
          <template v-if="invoice.invoiceStatusId === InvoiceStatusNumeric.Draft">
            <div class="vr"></div>
            <ButtonHighlighted :disabled="currentCreditNoteAllocationState?.error" :icon="faHandPointUp" @click="approveInvoice">Approve</ButtonHighlighted>
            <ButtonSecondary :disabled="currentCreditNoteAllocationState?.error" outline="teal" :icon="faHandPointUp" @click="showApproveAndSendConfirmation">Approve & Send</ButtonSecondary>
          </template>
        </template>
        <template v-else>
          <ButtonSecondary :icon="faArrowLeft" @click="navigateToInvoiceDashboard">Back to invoicing dashboard</ButtonSecondary>
        </template>
      </template>

      <template #charges>
        <InvoiceSkeletonCharges ref="charges" v-if="assetDetails?.type != AssetType.Wheeling"
                                :editable-charge-types="[InvoiceStatusNumeric.Draft, InvoiceStatusNumeric.ActionRequired].includes(invoice.invoiceStatusId) ? [ChargeType.Usage, ChargeType.Recurring] : []"
                                :total-excluding-tax="invoice.totalAmountExcludingTax"
                                :total-including-tax="invoiceAmounts.amountDueInCents / 100"
                                :tax="invoice.tax"
                                :tax-percentage="invoice.taxPercentage"
                                :categories="invoice.categories"
                                :asset-code="invoice.assetCode"
                                :summary-type="'all'" />

        <InvoiceSkeletonWheelingCharges ref="charges" v-else
          :asset-code="invoice.assetCode"
          :invoice-id="invoice.id"
          :capabilities="invoice.isManualInvoice ? [`add-charge`] : []"
          :categories="invoice.categories"
          :editable-charge-types="
            invoice.isManualInvoice && [InvoiceStatusNumeric.Draft, InvoiceStatusNumeric.ActionRequired].includes(invoice.invoiceStatusId)
              ? [ChargeType.WheelingUsageFirm, ChargeType.WheelingUsageNonFirm, ChargeType.WheelingUsageCapped, ChargeType.RenewableEnergyCreditAdminFee, ChargeType.WheelingGeneration, ChargeType.WheelingPenaltyDeemedEnergy]
              : []"/>
      </template>

      <template #meters v-if="assetDetails?.type !== AssetType.Wheeling">
        <InvoiceSkeletonMeterReadings ref="meters"
                                      :invoice-id="invoiceId"
                                      :invoice-status="invoice.invoiceStatusId"
                                      :exported="invoice.isExportPower"
                                      v-model:show="showMeters"
                                      :grid-consumption="invoice.totalGridConsumption"
                                      :solar-consumption="invoice.totalEnergyConsumption"
                                      :exported-energy="invoice.totalEnergyExported"
                                      :meters="invoice.meterReadings" />
      </template>

      <template #customer-details>
        <InvoiceSkeletonCustomerDetails :name="invoice.customer.name"
                                        :address="[
                                          invoice.customer.addressLineOne,
                                          invoice.customer.addressLineTwo,
                                          invoice.customer.suburb,
                                          invoice.customer.city,
                                          invoice.customer.region,
                                          invoice.customer.country,
                                        ]"
                                        :vat-number="invoice.customer.vatNumber"
                                        v-model:po-number="poNumber"
                                        :asset-type="assetDetails?.type"/>
      </template>

      <template #bill-total>
        <InvoiceSkeletonBillTotal :amount="invoiceAmounts.amountDueInCents / 100"
                                  :due-date="invoice.dueDate" />
      </template>

      <template #owner-details>
        <InvoiceSkeletonOwnerDetails :name="invoice.contractFundEntity.name"
                                     :address="invoice.contractFundEntity.address"
                                     :logo-url="invoice.contractFundEntity.logoUrl"
                                     :vat-number="invoice.contractFundEntity.vatNumber" />
      </template>

      <template #comments>
        <InvoiceSkeletonNotes v-model:content="notes" />
      </template>

      <template #invoice-details>
        <InvoiceSkeletonInvoiceDetails :invoice-number="invoice.invoiceNumber"
                                       :date="invoice.invoiceDate"
                                       :period="[invoice.periodStartDate, invoice.periodEndDate]"
                                       :date-format="invoice.invoiceDateFormat"
                                       :is-manual-invoice="invoice.isManualInvoice"
                                       :recs-enabled="invoice.areRecsEnabled"
                                       v-model:recs-generated="recsGenerated"/>
      </template>
      <template #banking-details>
        <InvoiceSkeletonBankingDetails :account-holder="invoice.contractFundEntity.accountName"
                                       :bank="invoice.contractFundEntity.bank"
                                       :account-number="invoice.contractFundEntity.accountNumber"
                                       :branch-code="invoice.contractFundEntity.branchCode" />
      </template>

      <template #summary v-if="assetDetails?.type !== AssetType.Wheeling">
        <InvoiceSkeletonSummary v-model:show="showSavings"
                                :savings-to-date="invoice.totalSavingsToDate"
                                :savings="invoice.invoiceSavings?.amount ?? 0"
                                :amount-exc-tax="invoice.totalAmountExcludingTax" />
      </template>

      <template #summary-full-width v-else>
        <InvoiceSkeletonSummaryFullWidth :amount-excluding-tax-in-cents="invoice.totalAmountExcludingTax * 100"
                                         :amount-including-tax-in-cents="invoice.totalAmountIncludingTax * 100"
                                         :credit-amount-in-cents="invoiceAmounts.totalCreditedAmountInCents"
                                         :tax-percentage="invoice.taxPercentage"
                                         :tax-amount-in-cents="invoice.tax * 100"
                                         :amount-due-in-cents="invoiceAmounts.amountDueInCents" />
      </template>

      <template #credit-notes v-if="assetDetails?.type === AssetType.Wheeling">
        <InvoiceSkeletonCreditNotes ref="creditNotes"
                                    :invoice-id="invoice.id"
                                    :invoice-status="invoice.invoiceStatusId"
                                    :invoice-amount-in-cents="invoice.totalAmountIncludingTax * 100"
                                    @update:allocated="currentCreditNoteAllocationState = $event" />
      </template>
    </InvoiceSkeleton>
  </PageContent>

  <InvoiceApprovalAffectedByCreditNoteErrorsModal v-if="approvalWarningModal.isOpen"
                                            v-bind="approvalWarningModal.state"
                                            @close="approvalWarningModal.close" />
</template>

<style scoped lang="scss">

</style>
